import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@emotion/react';

import {
  validateToken,
  validateTokenComplete,
  sessionTimeout,
  dismissSessionTimeout,
  setInitialProps,
} from 'data_system/token_auth/actions';
import {
  getTokenValidating,
  getUser,
  getSessionTimeout,
  getLoggedIn,
} from 'data_system/token_auth/selectors';

import { useInactivity } from 'ui_system/hooks/useInactivity';

import { getResourceI18n } from 'data_system/resource_portal/selectors';
import { fetchResource } from 'data_system/resource_portal/actions/resourceActions';

import { setContrastColor } from 'ui_system/theme/color_util';
import { SessionTimeoutModal } from 'ui_system/token_auth/SessionTimeoutModal';
import { Background } from 'ui_system/token_auth/styles_root';

import { translateWithObject, loadLocales } from 'services/translation';
import Router from './routes/Router';
import Header from './components/Header';
import TabNavigation from './components/TabNavigation';

const ResourcePortal = ({ brand, partner }) => {
  const dispatch = useDispatch();

  const { name } = brand;
  const updatedBrand = {
    ...brand,
    buttonTextColor: setContrastColor(brand.buttonBackgroundColor),
  };

  const tokenValidating = useSelector(getTokenValidating);
  const resource = useSelector(getUser);
  const loggedIn = useSelector(getLoggedIn);

  const isSessionTimedOut = useSelector(getSessionTimeout);
  const resourceI18n = useSelector(getResourceI18n);

  const t = translateWithObject(partner.translations);

  const i18n = useMemo(() => {
    const locale = new URL(window.location.href).searchParams.get('lang');
    const i18n = locale || resourceI18n || 'en-US';
    loadLocales(i18n);
    return i18n;
  }, [resourceI18n]);

  // Set title once for app
  useEffect(() => {
    document.title = `${name} - Resource Portal`;
  }, []);

  useEffect(() => {
    dispatch(
      setInitialProps({
        brand: updatedBrand,
        partner,
      })
    );

    if (localStorage.getItem('access-token')) {
      dispatch(validateToken);
      dispatch(fetchResource());
    } else {
      // Prevent unnecessary API request if we already know they're signed out
      dispatch(validateTokenComplete);
    }
  }, []);

  useInactivity(loggedIn, () => dispatch(sessionTimeout), 120);

  return (
    <ThemeProvider theme={updatedBrand}>
      <Background hidden={tokenValidating} white={resource}>
        <SessionTimeoutModal
          isOpen={isSessionTimedOut}
          onClose={() => dispatch(dismissSessionTimeout)}
        />
        <Header brand={brand} partner={partner} resource={resource} />
        <TabNavigation t={t} />
        {!tokenValidating && <Router i18n={i18n} />}
      </Background>
    </ThemeProvider>
  );
};

export default ResourcePortal;
